import React, { useCallback, useMemo } from 'react';
import { ScopeConfiguration } from './scopeUtilsLib';
import { useGetDepartmentsQuery, useGetStoresQuery } from '../../../apis/apiLists';
import { getStoreDepartmentConfigurationColumns } from './storeDepartmentColumns';
import { useApplicationData } from '../../../hooks/useApplicationData';
import { TablePaginationConfig } from 'antd/lib';
import { DataTable, useInjectLocalFilters, YColumnsType } from '@ydistri/ds';
import { ApplicationDataType } from '@ydistri/utils';
import { ConfigurationRuleSetupType, StoreResponse } from '@ydistri/api-sdk';
import {
  EntityColumns,
  useStoreTableColumnsProvider,
} from '../../../hooks/useEntityTableColumnsProvider';
import { computeRemSize } from '@ydistri/theme';
import { useTemplateOrCalculationWithValue } from '../../../hooks/useTemplateOrCalculationWithValue';
import { useGetFinalStoreDepartmentConfigurationQuery } from './apiScopes';

export interface StoreDepartmentTableRow extends ScopeConfiguration {
  store: Pick<StoreResponse, 'customerStoreId' | 'name' | 'code' | 'id'>;
  departmentId: number;
  departmentName: string;
  storeTypeName: string;
  regionName: string;
}

/**
 * The store x department matrix as the server resolves it, after every exception has been applied -
 * the same view the calculation runs with, not a copy computed on the client.
 */
const StoreDepartmentTable: React.FC = () => {
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const { [ApplicationDataType.DEPARTMENTS]: hasDepartments } = useApplicationData();

  const storeColumnsProvider = useStoreTableColumnsProvider();

  const { data: availableStores, isFetching: isFetchingStores } = useGetStoresQuery(
    templateOrCalculation.value,
  );
  const { data: availableDepartments, isFetching: isFetchingDepartments } = useGetDepartmentsQuery(
    templateOrCalculation.value,
  );
  const { data: resolved, isFetching: isFetchingResolved } =
    useGetFinalStoreDepartmentConfigurationQuery(templateOrCalculation.value);

  const loading = isFetchingStores || isFetchingDepartments || isFetchingResolved;

  const data: StoreDepartmentTableRow[] = useMemo(() => {
    if (loading || !resolved) return [];

    return resolved.map(row => {
      const storeObject = availableStores?.object[row.warehouseId];
      return {
        [ConfigurationRuleSetupType.UseAsSource]: row.useAsSource,
        [ConfigurationRuleSetupType.UseAsTarget]: row.useAsTarget,
        [ConfigurationRuleSetupType.IsEnabled]: row.isEnabled,
        [ConfigurationRuleSetupType.MaxNumberOfSourceStores]: row.maxNumberOfSourceStores,
        [ConfigurationRuleSetupType.MaxNumberOfTargetStores]: row.maxNumberOfTargetStores,
        [ConfigurationRuleSetupType.MaxNumberOfSourcePickingPositions]:
          row.maxNumberOfSourcePickingPositions,
        [ConfigurationRuleSetupType.MinimumRouteValue]: row.minimumRouteValue,
        store: {
          id: row.warehouseId,
          customerStoreId: storeObject?.customerStoreId ?? '',
          code: storeObject?.code ?? '',
          name: storeObject?.name ?? ` - Unknown store ${row.warehouseId} -`,
        },
        departmentId: row.departmentId,
        departmentName:
          availableDepartments?.object[row.departmentId]?.name ??
          ` - Unknown department ${row.departmentId} -`,
        storeTypeName: storeObject?.storeType?.name ?? ' - Unknown store type -',
        regionName: storeObject?.region?.name ?? ' - Unknown region -',
      };
    });
  }, [loading, resolved, availableDepartments?.object, availableStores?.object]);

  const columns = useMemo(() => {
    const codeStoreColumnConfig: YColumnsType<StoreDepartmentTableRow> = {
      width: '8rem',
    };

    const idStoreColumnConfig: YColumnsType<StoreDepartmentTableRow> = {
      width: computeRemSize(70),
      showSorterTooltip: false,
    };

    const storeColumnsConfig = {
      [EntityColumns.CODE]: codeStoreColumnConfig,
      [EntityColumns.ID]: idStoreColumnConfig,
    };
    const storeColumns = storeColumnsProvider<StoreDepartmentTableRow>(storeColumnsConfig);

    return getStoreDepartmentConfigurationColumns(hasDepartments, storeColumns);
  }, [storeColumnsProvider, hasDepartments]);

  const filterableColumns = useInjectLocalFilters(columns);

  const pagination: TablePaginationConfig = useMemo(
    () => ({
      showTotal: (total, range) =>
        `Showing ${range[0]}-${range[1]} of ${total} store${
          hasDepartments ? ' department' : ''
        }s available`,
      pageSize: 15,
      showSizeChanger: false,
      size: 'default',
      position: ['bottomLeft'],
    }),
    [hasDepartments],
  );

  const rowKeySelector = useCallback(
    (r: StoreDepartmentTableRow) => `${r.store.id}-${r.departmentId}`,
    [],
  );

  return (
    <DataTable<StoreDepartmentTableRow>
      showBorder={true}
      showHeader={true}
      id="store-configurations"
      loading={loading}
      columns={filterableColumns}
      dataSource={data}
      size="small"
      pagination={pagination}
      rowKey={rowKeySelector}
    />
  );
};

export default StoreDepartmentTable;
